/**
 * Centralized Parameter Display Name Utility
 *
 * This utility provides standardized display names for all health parameters
 * as the single source of truth, reading directly from parameters.md structure.
 *
 * Supports internationalization with Vietnamese translations.
 *
 * NO FALLBACK MECHANISMS - All parameter keys must be explicitly defined.
 * Clear error handling when parameter keys are not found.
 *
 * IMPORTANT STANDARDS CLARIFICATION:
 * - parameters.md includes units in parentheses (e.g., "Temperature (°C)")
 * - This implementation currently excludes units for cleaner display
 * - Units are handled separately by unitConversions.js for dual-unit display
 * - This approach provides flexibility for different UI contexts
 * - Both approaches are valid depending on UI requirements
 */

import i18n from '../i18n';
import { getParameterDisplayNameVi, hasVietnameseTranslation } from './parameterDisplayNamesVi';

// Parameter display name mappings extracted from parameters.md
// This will be the authoritative source for all parameter display names
const PARAMETER_DISPLAY_NAMES = {
  // === VITALS ===
  'vitals_temp_c': 'Temperature',
  'vitals_sbp': 'Systolic Blood Pressure',
  'vitals_dbp': 'Diastolic Blood Pressure',
  'vitals_rhr': 'Resting Heart Rate',
  'vitals_rr': 'Respiratory Rate',
  'vitals_map': 'Mean Arterial Pressure',
  'vitals_pp': 'Pulse Pressure',
  'vitals_spo2': 'Oxygen Saturation',
  'vitals_sirs': 'SIRS Score',
  'vitals_hrv': 'Heart Rate Variability',
  'vitals_vo2max': 'VO₂ Max',
  'vitals_pain': 'Pain Scale',
  'vitals_weight': 'Weight',
  'vitals_height': 'Height',
  'vitals_bmi': 'Body Mass Index',

  // === BIOMETRICS - COGNITION ===
  'biometrics_cognition_tmtA_time': 'Trail Making Test A - Time',
  'biometrics_cognition_tmtA_errors': 'Trail Making Test A - Errors',
  'biometrics_cognition_tmtB_time': 'Trail Making Test B - Time',
  'biometrics_cognition_tmtB_errors': 'Trail Making Test B - Errors',
  'biometrics_cognition_wordRecall_immediate1': 'Word Recall - Immediate Trial 1',
  'biometrics_cognition_wordRecall_immediate2': 'Word Recall - Immediate Trial 2',
  'biometrics_cognition_wordRecall_delayed': 'Word Recall - Delayed',
  'biometrics_cognition_stroop_word_time': 'Stroop Test - Word Time',
  'biometrics_cognition_stroop_word_errors': 'Stroop Test - Word Errors',
  'biometrics_cognition_stroop_color_time': 'Stroop Test - Color Time',
  'biometrics_cognition_stroop_color_errors': 'Stroop Test - Color Errors',
  'biometrics_cognition_stroop_incongruent_time': 'Stroop Test - Incongruent Time',
  'biometrics_cognition_stroop_incongruent_errors': 'Stroop Test - Incongruent Errors',
  'biometrics_cognition_stroop_interference_time': 'Stroop Test - Interference Time',
  'biometrics_cognition_stroop_interference_errors': 'Stroop Test - Interference Errors',

  // === BIOMETRICS - VISION ===
  'biometrics_vision_visualAcuity_distance_right': 'Visual Acuity - Distance Right Eye',
  'biometrics_vision_visualAcuity_distance_left': 'Visual Acuity - Distance Left Eye',
  'biometrics_vision_visualAcuity_distance_both': 'Visual Acuity - Distance Both Eyes',
  'biometrics_vision_visualAcuity_near_right': 'Visual Acuity - Near Right Eye',
  'biometrics_vision_visualAcuity_near_left': 'Visual Acuity - Near Left Eye',
  'biometrics_vision_visualAcuity_near_both': 'Visual Acuity - Near Both Eyes',
  'biometrics_vision_visualAcuity_pinhole_right': 'Visual Acuity - Pinhole Right Eye',
  'biometrics_vision_visualAcuity_pinhole_left': 'Visual Acuity - Pinhole Left Eye',

  // === BIOMETRICS - PULMONARY ===
  'biometrics_pulmonary_fev1': 'Forced Expiratory Volume in 1 Second',
  'biometrics_pulmonary_fvc': 'Forced Vital Capacity',
  'biometrics_pulmonary_pefr': 'Peak Expiratory Flow Rate',
  'biometrics_pulmonary_fev1_predicted': 'FEV₁ Predicted',
  'biometrics_pulmonary_fef25_75': 'Forced Expiratory Flow 25-75%',
  'biometrics_pulmonary_fev1_fvc_ratio': 'FEV₁/FVC Ratio',
  'biometrics_pulmonary_fvc_predicted': 'FVC Predicted',
  'biometrics_pulmonary_pefr_predicted': 'PEFR Predicted',
  'biometrics_pulmonary_lung_age_estimate': 'Lung Age Estimate',

  // === BIOMETRICS - BIOMEASUREMENT ===
  'biometrics_biomeasurement_waistCircumference': 'Waist Circumference',
  'biometrics_biomeasurement_hipCircumference': 'Hip Circumference',
  'biometrics_biomeasurement_neckCircumference': 'Neck Circumference',
  'biometrics_biomeasurement_weight_inbody': 'InBody Weight',
  'biometrics_biomeasurement_skeletalMuscleMass': 'Skeletal Muscle Mass',
  'biometrics_biomeasurement_bodyFatMass': 'Body Fat Mass',
  'biometrics_biomeasurement_percentBodyFat': 'Body Fat Percentage',
  'biometrics_biomeasurement_waistHipRatio': 'Waist-Hip Ratio',
  'biometrics_biomeasurement_visceralFatLevel': 'Visceral Fat Level',
  'biometrics_biomeasurement_softLeanMass': 'Soft Lean Mass',
  'biometrics_biomeasurement_upperLowerBodyBalance': 'Upper/Lower Body Balance',
  'biometrics_biomeasurement_inbodyScore': 'InBody Score',
  'biometrics_biomeasurement_bodyTypeCoordinate': 'Body Type Coordinate',
  'biometrics_biomeasurement_ree': 'Resting Energy Expenditure',
  'biometrics_biomeasurement_tee': 'Total Energy Expenditure',

  // === BIOMETRICS - FLEXIBILITY ===
  'biometrics_flexibility_armReach_back_right': 'Back Scratch Test - Right Arm',
  'biometrics_flexibility_armReach_back_left': 'Back Scratch Test - Left Arm',
  'biometrics_flexibility_legReach_sitting': 'Sit-and-Reach Test',

  // === BIOMETRICS - BALANCE ===
  'biometrics_balance_ybt_right_anterior_1': 'Y-Balance Test - Right Anterior Trial 1',
  'biometrics_balance_ybt_left_anterior_1': 'Y-Balance Test - Left Anterior Trial 1',
  'biometrics_balance_ybt_right_posteromedial_1': 'Y-Balance Test - Right Posteromedial Trial 1',
  'biometrics_balance_ybt_left_posteromedial_1': 'Y-Balance Test - Left Posteromedial Trial 1',
  'biometrics_balance_ybt_right_posterolateral_1': 'Y-Balance Test - Right Posterolateral Trial 1',
  'biometrics_balance_ybt_left_posterolateral_1': 'Y-Balance Test - Left Posterolateral Trial 1',
  'biometrics_balance_ybt_right_anterior_2': 'Y-Balance Test - Right Anterior Trial 2',
  'biometrics_balance_ybt_left_anterior_2': 'Y-Balance Test - Left Anterior Trial 2',
  'biometrics_balance_ybt_right_posteromedial_2': 'Y-Balance Test - Right Posteromedial Trial 2',
  'biometrics_balance_ybt_left_posteromedial_2': 'Y-Balance Test - Left Posteromedial Trial 2',
  'biometrics_balance_ybt_right_posterolateral_2': 'Y-Balance Test - Right Posterolateral Trial 2',
  'biometrics_balance_ybt_left_posterolateral_2': 'Y-Balance Test - Left Posterolateral Trial 2',
  'biometrics_balance_ybt_right_anterior_avg': 'Y-Balance Test - Right Anterior Average',
  'biometrics_balance_ybt_left_anterior_avg': 'Y-Balance Test - Left Anterior Average',
  'biometrics_balance_ybt_right_posteromedial_avg': 'Y-Balance Test - Right Posteromedial Average',
  'biometrics_balance_ybt_left_posteromedial_avg': 'Y-Balance Test - Left Posteromedial Average',
  'biometrics_balance_ybt_right_posterolateral_avg': 'Y-Balance Test - Right Posterolateral Average',
  'biometrics_balance_ybt_left_posterolateral_avg': 'Y-Balance Test - Left Posterolateral Average',
  'biometrics_balance_ybt_right_limb_length': 'Y-Balance Test - Right Limb Length',
  'biometrics_balance_ybt_left_limb_length': 'Y-Balance Test - Left Limb Length',
  'biometrics_balance_ybt_right_composite_reach': 'Y-Balance Test - Right Composite Reach',
  'biometrics_balance_ybt_left_composite_reach': 'Y-Balance Test - Left Composite Reach',
  'biometrics_balance_ybt_composite_deviation': 'Y-Balance Test - Composite Deviation',
  'biometrics_balance_ybt_anterior_deviation': 'Y-Balance Test - Anterior Deviation',
  'biometrics_balance_ybt_posteromedial_deviation': 'Y-Balance Test - Posteromedial Deviation',
  'biometrics_balance_ybt_posterolateral_deviation': 'Y-Balance Test - Posterolateral Deviation',
  'biometrics_balance_ybt_performance_ratio': 'Y-Balance Test Performance Ratio',

  // === BIOMETRICS - STRENGTH ===
  'biometrics_strength_handgrip_right_1': 'Handgrip Strength - Right Trial 1',
  'biometrics_strength_handgrip_right_2': 'Handgrip Strength - Right Trial 2',
  'biometrics_strength_handgrip_left_1': 'Handgrip Strength - Left Trial 1',
  'biometrics_strength_handgrip_left_2': 'Handgrip Strength - Left Trial 2',
  'biometrics_strength_handgrip_right_avg': 'Handgrip Strength - Right Average',
  'biometrics_strength_handgrip_left_avg': 'Handgrip Strength - Left Average',
  'biometrics_strength_handgrip_normative_status': 'Handgrip Strength - Normative Status',
  'biometrics_strength_handgrip_deviation_percent': 'Handgrip Strength - Deviation',
  'biometrics_strength_handgrip_relative_strength': 'Handgrip Strength - Relative (kg/kg body weight)',
  'biometrics_strength_pushups_completed': 'Push-ups Completed',

  // === BIOMETRICS - ENDURANCE ===
  'biometrics_endurance_wallsit_duration': 'Wall Sit Duration',
  'biometrics_endurance_pushups_completed': 'Push-ups Completed',
  'biometrics_endurance_step_test_duration': 'Step Test Duration',
  'biometrics_endurance_heart_rate_peak': 'Peak Heart Rate',
  'biometrics_endurance_heart_rate_1min': 'Heart Rate at 1 Minute',
  'biometrics_endurance_heart_rate_1min_post_exercise': 'Heart Rate 1 Min Post-Exercise',

  // === BIOMETRICS - POWER ===
  'biometrics_power_jump_best': 'Vertical Jump - Best',
  'biometrics_power_jump_percent_max': 'Vertical Jump - Percent Max',
  'biometrics_power_jump_average_height': 'Vertical Jump - Average Height',
  'biometrics_power_landing_impact': 'Landing Impact Force',
  'biometrics_power_power_watts': 'Power Output',

  // === PHYSICAL EXAM - INTEGUMENTARY ===
  'pe_integumentary_alopecia': 'Alopecia (Hair Loss)',
  'pe_integumentary_scalp_lesions': 'Scalp Lesions',
  'pe_integumentary_arm_lesions': 'Arm Lesions',
  'pe_integumentary_leg_lesions': 'Leg Lesions',
  'pe_integumentary_finger_nails': 'Finger Nails',
  'pe_integumentary_toe_nails': 'Toe Nails',
  'pe_integumentary_skin_turgor': 'Skin Turgor',
  'pe_integumentary_jaundice': 'Jaundice',
  'pe_integumentary_pallor': 'Pallor',
  'pe_integumentary_bruising': 'Bruising',
  'pe_integumentary_surgical_scar': 'Surgical Scar',
  'pe_integumentary_caput_medusa': 'Caput Medusae',

  // === PHYSICAL EXAM - LYMPH NODES ===
  'pe_lymph_occipital_nodes': 'Occipital Lymph Nodes',
  'pe_lymph_periauricular_nodes': 'Periauricular Lymph Nodes',
  'pe_lymph_cervical_nodes': 'Cervical Lymph Nodes',
  'pe_lymph_submental_nodes': 'Submental Lymph Nodes',
  'pe_lymph_submandibular_nodes': 'Submandibular Lymph Nodes',
  'pe_lymph_supraclavicular_nodes': 'Supraclavicular Lymph Nodes',
  'pe_lymph_femoral_nodes': 'Femoral Lymph Nodes',
  'pe_lymph_popliteal_nodes': 'Popliteal Lymph Nodes',

  // === PHYSICAL EXAM - HEENT EYES ===
  'pe_heent_eyes_conjunctiva': 'Conjunctiva',
  'pe_heent_eyes_sclera': 'Sclera',
  'pe_heent_eyes_ocular_shape': 'Ocular Shape',
  'pe_heent_eyes_vision_field': 'Visual Field',
  'pe_heent_eyes_light_reflex': 'Light Reflex',
  'pe_heent_eyes_red_reflex': 'Red Reflex',
  'pe_heent_eyes_h_test': 'H-Test (Extraocular Movements)',
  'pe_heent_eyes_finger_to_nose': 'Finger-to-Nose Test',

  // === PHYSICAL EXAM - HEENT EARS ===
  'pe_heent_ears_external_ear_canal': 'External Ear Canal',
  'pe_heent_ears_tympanic_membrane': 'Tympanic Membrane',
  'pe_heent_ears_whisper_test': 'Whisper Test',
  'pe_heent_ears_rinne_test': 'Rinne Test',
  'pe_heent_ears_weber_test': 'Weber Test',

  // === PHYSICAL EXAM - HEENT NOSE/SINUS ===
  'pe_heent_nose_nasal_turbinate': 'Nasal Turbinate',
  'pe_heent_nose_nasal_polyp': 'Nasal Polyp',
  'pe_heent_nose_nasal_septum_deviation': 'Nasal Septum Deviation',
  'pe_heent_nose_maxillary_sinus': 'Maxillary Sinus',
  'pe_heent_nose_frontal_sinus': 'Frontal Sinus',

  // === PHYSICAL EXAM - HEENT MOUTH/THROAT ===
  'pe_heent_mouth_oral_mucosa': 'Oral Mucosa',
  'pe_heent_mouth_san_mieng': 'Gingiva',
  'pe_heent_mouth_vom_mieng': 'Palate',
  'pe_heent_mouth_luoi': 'Tongue',
  'pe_heent_mouth_amidan': 'Tonsils',
  'pe_heent_mouth_mallampati': 'Mallampati Score',
  'pe_heent_mouth_uvula_position': 'Uvula Position',
  'pe_heent_mouth_tmj_tenderness': 'TMJ Tenderness',
  'pe_heent_mouth_maxillary_teeth': 'Maxillary Teeth',
  'pe_heent_mouth_mandibular_teeth': 'Mandibular Teeth',
  'pe_heent_mouth_cough_reflex': 'Cough Reflex',

  // === PHYSICAL EXAM - NECK ===
  'pe_neck_thyroid': 'Thyroid',

  // === PHYSICAL EXAM - CHEST/RESPIRATORY ===
  'pe_chest_resp_chest_expansion_symmetry': 'Chest Expansion Symmetry',
  'pe_chest_resp_chest_expansion_range': 'Chest Expansion Range',
  'pe_chest_resp_crackles': 'Crackles',
  'pe_chest_resp_wheezing': 'Wheezing',
  'pe_chest_resp_rhonchi': 'Rhonchi',
  'pe_chest_resp_stridor': 'Stridor',

  // === PHYSICAL EXAM - CARDIOVASCULAR ===
  'pe_cardio_heart_rhythm': 'Heart Rhythm',
  'pe_cardio_s1': 'S1 Heart Sound',
  'pe_cardio_s2': 'S2 Heart Sound',
  'pe_cardio_s3': 'S3 Heart Sound',
  'pe_cardio_s4': 'S4 Heart Sound',
  'pe_cardio_systolic_murmur': 'Systolic Murmur',
  'pe_cardio_diastolic_murmur': 'Diastolic Murmur',
  'pe_cardio_apical_impulse': 'Apical Impulse',
  'pe_cardio_carotid_bruit': 'Carotid Bruit',
  'pe_cardio_hepatojugular_reflux': 'Hepatojugular Reflux',
  'pe_cardio_radial_pulse': 'Radial Pulse',
  'pe_cardio_cap_refill_finger': 'Capillary Refill - Finger',
  'pe_cardio_abdominal_bruit': 'Abdominal Bruit',
  'pe_cardio_posterior_tibial': 'Posterior Tibial Pulse',
  'pe_cardio_dorsalis_pedis': 'Dorsalis Pedis Pulse',
  'pe_cardio_pedal_edema': 'Pedal Edema',
  'pe_cardio_cap_refill_toe': 'Capillary Refill - Toe',
  'pe_cardio_varicose_veins': 'Varicose Veins',

  // === PHYSICAL EXAM - ABDOMEN ===
  'pe_abdomen_bowel_sound': 'Bowel Sounds',
  'pe_abdomen_abdominal_distension': 'Abdominal Distension',
  'pe_abdomen_hepatomegaly': 'Hepatomegaly',
  'pe_abdomen_splenomegaly': 'Splenomegaly',
  'pe_abdomen_epigastric_tenderness': 'Epigastric Tenderness',
  'pe_abdomen_luq_tenderness': 'Left Upper Quadrant Tenderness',
  'pe_abdomen_ruq_tenderness': 'Right Upper Quadrant Tenderness',
  'pe_abdomen_llq_tenderness': 'Left Lower Quadrant Tenderness',
  'pe_abdomen_rlq_tenderness': 'Right Lower Quadrant Tenderness',
  'pe_abdomen_suprapubic_tenderness': 'Suprapubic Tenderness',
  'pe_abdomen_cva_tenderness': 'Costovertebral Angle Tenderness',

  // === PHYSICAL EXAM - MUSCULOSKELETAL ===
  'pe_msk_cervical_lordosis': 'Cervical Lordosis',
  'pe_msk_thoracic_kyphosis': 'Thoracic Kyphosis',
  'pe_msk_lumbar_lordosis': 'Lumbar Lordosis',
  'pe_msk_visible_scoliosis': 'Visible Scoliosis',
  'pe_msk_cervical_tenderness': 'Cervical Tenderness',
  'pe_msk_thoracic_tenderness': 'Thoracic Tenderness',
  'pe_msk_lumbar_tenderness': 'Lumbar Tenderness',
  'pe_msk_cervical_flexion': 'Cervical Flexion',
  'pe_msk_cervical_extension': 'Cervical Extension',
  'pe_msk_cervical_rotation': 'Cervical Rotation',
  'pe_msk_lumbar_flexion': 'Lumbar Flexion',
  'pe_msk_lumbar_extension': 'Lumbar Extension',
  'pe_msk_shoulder_height_symmetry': 'Shoulder Height Symmetry',
  'pe_msk_shoulder_abduction_arom': 'Shoulder Abduction AROM',
  'pe_msk_hip_height_symmetry': 'Hip Height Symmetry',
  'pe_msk_thomas_hip_test': 'Thomas Hip Test',
  'pe_msk_faber_test': 'FABER Test',
  'pe_msk_knee_alignment': 'Knee Alignment',
  'pe_msk_patellar_reflex': 'Patellar Reflex',

  // === PHYSICAL EXAM - NEUROLOGICAL ===
  'pe_neuro_ulnar_nerve_sensory': 'Ulnar Nerve Sensory',
  'pe_neuro_radial_nerve_sensory': 'Radial Nerve Sensory',
  'pe_neuro_medial_nerve_sensory': 'Median Nerve Sensory',
  'pe_neuro_pinprick_sensory': 'Pinprick Sensory',
  'pe_neuro_tandem_walk': 'Tandem Walk',
  'pe_neuro_romberg': 'Romberg Test',

  // === LABS - CARDIAC ===
  'labs_cardiac_troponin': 'Troponin-HS',
  'labs_cardiac_bnp': 'B-Type Natriuretic Peptide',
  'labs_cardiac_ckmb': 'Creatine Kinase-MB',

  // === LABS - CBC ===
  'labs_cbc_wbc': 'White Blood Cell Count',
  'labs_cbc_neut': 'Neutrophils',
  'labs_cbc_lymph': 'Lymphocytes',
  'labs_cbc_mono': 'Monocytes',
  'labs_cbc_eos': 'Eosinophils',
  'labs_cbc_baso': 'Basophils',
  'labs_cbc_neut_pct': 'Neutrophils',
  'labs_cbc_lymph_pct': 'Lymphocytes',
  'labs_cbc_mono_pct': 'Monocytes',
  'labs_cbc_eos_pct': 'Eosinophils',
  'labs_cbc_baso_pct': 'Basophils',
  'labs_cbc_rbc': 'Red Blood Cell Count',
  'labs_cbc_hgb': 'Hemoglobin',
  'labs_cbc_hct': 'Hematocrit',
  'labs_cbc_mcv': 'Mean Corpuscular Volume',
  'labs_cbc_mch': 'Mean Corpuscular Hemoglobin',
  'labs_cbc_mchc': 'Mean Corpuscular Hemoglobin Concentration',
  'labs_cbc_rdw': 'Red Cell Distribution Width',
  'labs_cbc_rdw_sd': 'Red Cell Distribution Width-SD',
  'labs_cbc_plt': 'Platelet Count',
  'labs_cbc_mpv': 'Mean Platelet Volume',
  'labs_cbc_pdw': 'Platelet Distribution Width',
  'labs_cbc_pct': 'Plateletcrit',
  'labs_cbc_p_lcr': 'Platelet Large Cell Ratio',
  'labs_cbc_nlr': 'Neutrophil-to-Lymphocyte Ratio',

  // === LABS - LIPID ===
  'labs_lipid_apob': 'Apolipoprotein B',
  'labs_lipid_chol_total': 'Total Cholesterol',
  'labs_lipid_hdl': 'High-Density Lipoprotein Cholesterol',
  'labs_lipid_ldl': 'Low-Density Lipoprotein Cholesterol',
  'labs_lipid_trig': 'Triglycerides',
  'labs_lipid_vldl': 'Very Low-Density Lipoprotein Cholesterol',
  'labs_lipid_non_hdl': 'Non-HDL Cholesterol',
  'labs_lipid_tg_hdl_ratio': 'Triglyceride/HDL Ratio',
  'labs_lipid_tc_glu_ratio': 'Total Cholesterol/Glucose Ratio',
  'labs_lipid_tc_hdl_ratio': 'Total Cholesterol/HDL Ratio',
  'labs_lipid_athero_idx': 'Atherogenic Index',

  // === LABS - ELECTROLYTES ===
  'labs_electrolytes_magnesium': 'Magnesium',
  'labs_electrolytes_phosphorous': 'Phosphorus',
  'labs_electrolytes_sodium': 'Sodium',
  'labs_electrolytes_potassium': 'Potassium',
  'labs_electrolytes_chloride': 'Chloride',
  'labs_electrolytes_calcium': 'Calcium',
  'labs_electrolytes_bicarb': 'Bicarbonate',
  'labs_electrolytes_osmolality': 'Osmolality',
  'labs_electrolytes_na_k_ratio': 'Sodium/Potassium Ratio',

  // === LABS - COAGULATION ===
  'labs_coagulation_pt': 'Prothrombin Time',
  'labs_coagulation_inr': 'International Normalized Ratio',
  'labs_coagulation_ptt': 'Partial Thromboplastin Time',
  'labs_coagulation_thrombin_time': 'Thrombin Time',
  'labs_coagulation_fibrinogen': 'Fibrinogen',
  'labs_coagulation_d_dimer': 'D-Dimer',

  // === LABS - GLUCOSE ===
  'labs_glucose_hba1c': 'Hemoglobin A1c (HbA1c)',
  'labs_glucose_glucose': 'Glucose',

  // === LABS - ADDITIONAL ===

  // === LABS - VITAMINS ===
  'labs_vitamins_iron': 'Iron',
  'labs_vitamins_b12': 'Vitamin B12',
  'labs_vitamins_d3': 'Vitamin D3',
  'labs_vitamins_b9': 'Folate (Vitamin B9)',
  'labs_vitamins_ferritin': 'Ferritin',

  // === LABS - RENAL ===
  'labs_renal_creatinine': 'Creatinine',
  'labs_renal_egfr': 'Estimated Glomerular Filtration Rate',
  'labs_renal_urea': 'Urea',
  'labs_renal_bun': 'Blood Urea Nitrogen',

  // === LABS - INFLAMMATION ===
  'labs_inflammation_uric_acid': 'Uric Acid',
  'labs_inflammation_hscrp': 'hsCRP',
  'labs_inflammation_esr': 'ESR',

  // === LABS - HEPATOLOGY ===
  'labs_hepatology_ast': 'Aspartate Aminotransferase',
  'labs_hepatology_alt': 'Alanine Aminotransferase',
  'labs_hepatology_ggt': 'Gamma-Glutamyl Transferase',
  'labs_hepatology_bili_total': 'Total Bilirubin',
  'labs_hepatology_bili_direct': 'Direct Bilirubin',
  'labs_hepatology_bili_indirect': 'Indirect Bilirubin',
  'labs_hepatology_alp': 'Alkaline Phosphatase',

  // === LABS - PROTEIN ===
  'labs_protein_total': 'Total Protein',
  'labs_protein_albumin': 'Albumin',
  'labs_protein_prealbumin': 'Prealbumin',
  'labs_protein_alb_glob_ratio': 'Albumin/Globulin Ratio',

  // === LABS - THYROID ===
  'labs_thyroid_ft4': 'Free Thyroxine (T4)',
  'labs_thyroid_tsh': 'Thyroid Stimulating Hormone',
  'labs_thyroid_ft3': 'Free Triiodothyronine (T3)',

  // === LABS - URINE ===
  'labs_urine_sg': 'Specific Gravity',
  'labs_urine_ph': 'Urine pH',
  'labs_urine_nitrite': 'Nitrite',
  'labs_urine_protein': 'Protein',
  'labs_urine_glucose': 'Glucose',
  'labs_urine_ketones': 'Ketones',
  'labs_urine_urobil': 'Urobilinogen',
  'labs_urine_bili': 'Bilirubin',
  'labs_urine_ery': 'Erythrocytes',
  'labs_urine_leuk': 'Leukocytes',
  'labs_urine_microalb': 'Microalbumin (mg/L)',
  'labs_urine_creat': 'Creatinine',
  'labs_urine_uacr': 'Urine Albumin-to-Creatinine Ratio',

  // === LIFESTYLE - NUTRITION ===
  'lifestyle_nutrition_protein_intake': 'Daily Protein Intake',
  'lifestyle_nutrition_fruit_intake': 'Daily Fruit Intake',
  'lifestyle_nutrition_vegetables_intake': 'Daily Vegetable Intake',
  'lifestyle_nutrition_refined_carbs_intake': 'Refined Carbohydrate Intake',
  'lifestyle_nutrition_fiber_intake': 'Daily Fiber Intake',
  'lifestyle_nutrition_seeds_nuts_intake': 'Seeds and Nuts Intake',
  'lifestyle_nutrition_sodium_intake': 'Daily Sodium Intake',
  'lifestyle_nutrition_sugar_intake': 'Daily Sugar Intake',
  'lifestyle_nutrition_fried_food_intake': 'Fried Food Intake',
  'lifestyle_nutrition_healthy_oil_use': 'Healthy Oil Use',
  'lifestyle_nutrition_ultraprocessed_intake': 'Ultra-Processed Food Intake',
  'lifestyle_nutrition_water_intake': 'Daily Water Intake',
  'lifestyle_nutrition_alcohol_intake': 'Alcohol Consumption',
  'lifestyle_nutrition_weight_loss_concern': 'Weight Loss Concern',
  'lifestyle_nutrition_weight_gain_concern': 'Weight Gain Concern',
  'lifestyle_nutrition_appetite_lower_normal': 'Appetite Lower Than Normal',
  'lifestyle_nutrition_appetite_higher_normal': 'Appetite Higher Than Normal',
  'lifestyle_nutrition_skip_meals_hungry': 'Skip Meals When Hungry',
  'lifestyle_nutrition_full_small_amount': 'Feel Full After Small Amount',
  'lifestyle_nutrition_difficulty_chewing_swallowing': 'Difficulty Chewing/Swallowing',
  'lifestyle_nutrition_problems_taste_smell': 'Problems with Taste/Smell',
  'lifestyle_nutrition_worry_food_access': 'Worry About Food Access',
  'lifestyle_nutrition_bored_eating': 'Eating When Bored',
  'lifestyle_nutrition_busy_eating': 'Eating When Busy',
  'lifestyle_nutrition_late_eating': 'Late Night Eating',
  'lifestyle_nutrition_binge_eating': 'Binge Eating',
  'lifestyle_nutrition_guilty_eating': 'Guilty Eating',
  'lifestyle_nutrition_rushed_eating': 'Rushed Eating',
  'lifestyle_nutrition_distracted_eating': 'Distracted Eating',
  'lifestyle_nutrition_fad_diets': 'Following Fad Diets',

  // === LIFESTYLE - MOVEMENT ===
  'lifestyle_movement_moderate_activity_days': 'Moderate Activity Days per Week',
  'lifestyle_movement_vigorous_activity_days': 'Vigorous Activity Days per Week',
  'lifestyle_movement_strength_training_days': 'Strength Training Days per Week',
  'lifestyle_movement_flexibility_stretching_days': 'Flexibility/Stretching Days per Week',
  'lifestyle_movement_balance_practice_days': 'Balance Practice Days per Week',
  'lifestyle_movement_incidental_walking_time': 'Incidental Walking Time',
  'lifestyle_movement_sedentary_hours': 'Daily Sedentary Hours',
  'lifestyle_movement_sitting_break_days': 'Sitting Break Days per Week',
  'lifestyle_movement_red_flag_sob': 'Exercise Red Flag - Shortness of Breath',
  'lifestyle_movement_red_flag_chest_pain': 'Exercise Red Flag - Chest Pain',
  'lifestyle_movement_red_flag_palpitations': 'Exercise Red Flag - Palpitations',
  'lifestyle_movement_red_flag_dizziness': 'Exercise Red Flag - Dizziness',
  'lifestyle_movement_red_flag_pain': 'Exercise Red Flag - Pain',
  'lifestyle_movement_red_flag_imbalance': 'Exercise Red Flag - Imbalance',

  // === LIFESTYLE - RECOVERY ===
  'lifestyle_recovery_sleep_quality': 'Sleep Quality Rating',
  'lifestyle_recovery_sleep_duration': 'Sleep Duration',
  'lifestyle_recovery_sleep_continuity': 'Sleep Continuity Rating',
  'lifestyle_recovery_sleep_onset': 'Sleep Onset Time',
  'lifestyle_recovery_bedtime_consistency': 'Bedtime Consistency',
  'lifestyle_recovery_late_bedtime': 'Late Bedtime Frequency',
  'lifestyle_recovery_sleep_environment_factors_uncomfortable_mattress': 'Sleep Environment - Uncomfortable Mattress',
  'lifestyle_recovery_sleep_environment_factors_room_temperature': 'Sleep Environment - Room Temperature',
  'lifestyle_recovery_sleep_environment_factors_bright_light': 'Sleep Environment - Bright Light',
  'lifestyle_recovery_sleep_environment_factors_loud_noises': 'Sleep Environment - Loud Noises',
  'lifestyle_recovery_sleep_environment_factors_electronic_screens': 'Sleep Environment - Electronic Screens',
  'lifestyle_recovery_sleep_environment_factors_caffeine_stimulants': 'Sleep Environment - Caffeine/Stimulants',
  'lifestyle_recovery_napping_frequency': 'Napping Frequency',
  'lifestyle_recovery_daytime_sleepiness': 'Daytime Sleepiness',
  'lifestyle_recovery_sleep_disorders_waking_drenched_sweats': 'Sleep Disorder - Night Sweats',
  'lifestyle_recovery_sleep_disorders_teeth_grinding': 'Sleep Disorder - Teeth Grinding',
  'lifestyle_recovery_sleep_disorders_nasal_congestion_snoring': 'Sleep Disorder - Snoring/Nasal Congestion',
  'lifestyle_recovery_sleep_disorders_restless_legs': 'Sleep Disorder - Restless Legs',
  'lifestyle_recovery_sleep_disorders_racing_thoughts': 'Sleep Disorder - Racing Thoughts',
  'lifestyle_recovery_sleep_disorders_upsetting_dreams': 'Sleep Disorder - Upsetting Dreams',

  // === IMAGING - SPLEEN US ===
  'imaging_spleen_us_craniocaudal_length': 'Spleen Craniocaudal Length',
  'imaging_spleen_us_capsular_edge_nodularity': 'Spleen Capsular Edge Nodularity',
  'imaging_spleen_us_parenchymal_echogenicity': 'Spleen Parenchymal Echogenicity',
  'imaging_spleen_us_largest_lesion_diameter': 'Spleen Largest Lesion Diameter',
  'imaging_spleen_us_splenic_vein_diameter': 'Splenic Vein Diameter',
  'imaging_spleen_us_psdr': 'Portal Splenic Diameter Ratio',

  // === IMAGING - LIVER US ===
  'imaging_liver_us_craniocaudal_length': 'Liver Craniocaudal Length',
  'imaging_liver_us_capsular_edge_nodularity': 'Liver Capsular Edge Nodularity',
  'imaging_liver_us_echogenicity_steatosis': 'Liver Echogenicity/Steatosis',
  'imaging_liver_us_portal_vein_diameter': 'Portal Vein Diameter',
  'imaging_liver_us_lirads': 'LI-RADS Score',
  'imaging_liver_us_largest_lesion_diameter': 'Liver Largest Lesion Diameter',
  'imaging_liver_us_hepatic_vein_waveform': 'Hepatic Vein Waveform',

  // === IMAGING - CAROTID US ===
  'imaging_carotid_us_l_cca_score': 'Left Common Carotid Artery Score',
  'imaging_carotid_us_r_cca_score': 'Right Common Carotid Artery Score',
  'imaging_carotid_us_l_bulb_score': 'Left Carotid Bulb Score',
  'imaging_carotid_us_r_bulb_score': 'Right Carotid Bulb Score',
  'imaging_carotid_us_l_ica_score': 'Left Internal Carotid Artery Score',
  'imaging_carotid_us_r_ica_score': 'Right Internal Carotid Artery Score',
  'imaging_carotid_us_l_cimt': 'Left Carotid Intima-Media Thickness',
  'imaging_carotid_us_r_cimt': 'Right Carotid Intima-Media Thickness',
  'imaging_carotid_us_plaque_presence': 'Carotid Plaque Presence',
  'imaging_carotid_us_surface_ulceration': 'Carotid Surface Ulceration',
  'imaging_carotid_us_calcification_pattern': 'Carotid Calcification Pattern',
  'imaging_carotid_us_l_ica_psv': 'Left ICA Peak Systolic Velocity',
  'imaging_carotid_us_r_ica_psv': 'Right ICA Peak Systolic Velocity',
  'imaging_carotid_us_l_ica_edv': 'Left ICA End Diastolic Velocity',
  'imaging_carotid_us_r_ica_edv': 'Right ICA End Diastolic Velocity',
  'imaging_carotid_us_vascular_age_imt': 'Vascular Age by IMT',
  'imaging_carotid_us_vascular_age_cac': 'Vascular Age by CAC',

  // === IMAGING - EKG ===
  'imaging_ekg_rhythm_abnormality': 'EKG Rhythm Abnormality',
  'imaging_ekg_axis_deviation': 'EKG Axis Deviation',
  'imaging_ekg_ventricular_hypertrophy': 'EKG Ventricular Hypertrophy',
  'imaging_ekg_conduction_abnormality': 'EKG Conduction Abnormality',
  'imaging_ekg_pr_interval': 'EKG PR Interval',
  'imaging_ekg_qrs_duration': 'EKG QRS Duration',
  'imaging_ekg_qtc_interval': 'EKG QTc Interval',
  'imaging_ekg_st_changes': 'EKG ST Changes',
  'imaging_ekg_t_wave_interval': 'EKG T Wave Changes',
  'imaging_ekg_ectopy': 'EKG Ectopy',

  // === IMAGING - BLADDER US ===
  'imaging_bladder_us_pre_void_volume': 'Bladder Pre-Void Volume (mL)',
  'imaging_bladder_us_pvr_volume': 'Bladder Post-Void Residual Volume (mL)',
  'imaging_bladder_us_wall_thickness': 'Bladder Wall Thickness',
  'imaging_bladder_us_calculi_presence': 'Bladder Calculi Presence',
  'imaging_bladder_us_calculi_description': 'Bladder Calculi Description',
  'imaging_bladder_us_masses_presence': 'Bladder Masses Presence',
  'imaging_bladder_us_masses_description': 'Bladder Masses Description',
  'imaging_bladder_us_ureteral_jets': 'Ureteral Jets',

  // === IMAGING - CHEST XR ===
  'imaging_chest_xr_date': 'Chest X-Ray Date',
  'imaging_chest_xr_pleural_effusion': 'Pleural Effusion',
  'imaging_chest_xr_enlarged_pulmonary_artery': 'Enlarged Pulmonary Artery',
  'imaging_chest_xr_enlarged_right_ventricle': 'Enlarged Right Ventricle',
  'imaging_chest_xr_flattened_diaphragm': 'Flattened Diaphragm',
  'imaging_chest_xr_cardiothoracic_ratio_gt_50': 'Cardiothoracic Ratio >50%',
  'imaging_chest_xr_cavitary_lung_lesion': 'Cavitary Lung Lesion',
  'imaging_chest_xr_widened_mediastinum': 'Widened Mediastinum',
  'imaging_chest_xr_enlarged_hilar_lymph_nodes': 'Enlarged Hilar Lymph Nodes',
  'imaging_chest_xr_atelectasis': 'Atelectasis',
  'imaging_chest_xr_pulmonary_infiltrates': 'Pulmonary Infiltrates',
  'imaging_chest_xr_pulmonary_nodule_or_mass': 'Pulmonary Nodule or Mass',
  'imaging_chest_xr_spinal_deviation': 'Spinal Deviation',
  'imaging_chest_xr_lytic_bone_lesion': 'Lytic Bone Lesion',

  // === IMAGING - HEART US ===
  'imaging_heart_us_ejection_fraction': 'Ejection Fraction',
  'imaging_heart_us_wall_motion': 'Wall Motion',
  'imaging_heart_us_valve_function': 'Valve Function',
  'imaging_heart_us_chamber_size': 'Chamber Size',
  'imaging_heart_us_pericardial_effusion': 'Pericardial Effusion',

  // === IMAGING - GALLBLADDER US ===
  'imaging_gallbladder_us_wall_thickness': 'Gallbladder Wall Thickness',
  'imaging_gallbladder_us_stones_present': 'Gallbladder Stones Present',
  'imaging_gallbladder_us_sludge_present': 'Gallbladder Sludge Present',
  'imaging_gallbladder_us_polyps_present': 'Gallbladder Polyps Present',
  'imaging_gallbladder_us_distension': 'Gallbladder Distension',

  // === IMAGING - KIDNEY US ===
  'imaging_kidney_us_right_size': 'Right Kidney Size',
  'imaging_kidney_us_left_size': 'Left Kidney Size',
  'imaging_kidney_us_echogenicity': 'Kidney Echogenicity',
  'imaging_kidney_us_hydronephrosis': 'Hydronephrosis',
  'imaging_kidney_us_stones_present': 'Kidney Stones Present',
  'imaging_kidney_us_masses_present': 'Kidney Masses Present',

  // === IMAGING - THYROID US ===
  'imaging_thyroid_us_date': 'Thyroid US Date',
  'imaging_thyroid_us_right_lobe_size': 'Right Lobe Size',
  'imaging_thyroid_us_left_lobe_size': 'Left Lobe Size',
  'imaging_thyroid_us_isthmus_thickness': 'Isthmus Thickness',
  'imaging_thyroid_us_echogenicity': 'Echogenicity',
  'imaging_thyroid_us_nodules_present': 'Nodules Present',
  'imaging_thyroid_us_largest_nodule_size': 'Largest Nodule Size',
  'imaging_thyroid_us_nodule_composition': 'Nodule Composition',
  'imaging_thyroid_us_calcifications': 'Calcifications',
  'imaging_thyroid_us_vascularity': 'Vascularity',
  'imaging_thyroid_us_lymph_nodes': 'Lymph Nodes',
  'imaging_thyroid_us_tirads_score': 'TI-RADS Score',

  // === LIFESTYLE - EMOTION ===
  'lifestyle_emotion_little_interest': 'Little Interest in Activities',
  'lifestyle_emotion_feeling_down': 'Feeling Down/Depressed',
  'lifestyle_emotion_feeling_nervous': 'Feeling Nervous/Anxious',
  'lifestyle_emotion_not_being_able': 'Not Being Able to Stop Worrying',
  'lifestyle_emotion_difficulty_concentrating': 'Difficulty Concentrating',
  'lifestyle_emotion_feeling_irritable': 'Feeling Irritable',
  'lifestyle_emotion_felt_unable_control': 'Felt Unable to Control Important Things',
  'lifestyle_emotion_felt_confident_problems': 'Felt Confident About Handling Problems',
  'lifestyle_emotion_felt_things_going_way': 'Felt Things Were Going Your Way',
  'lifestyle_emotion_felt_difficulties_piling_up': 'Felt Difficulties Were Piling Up',
  'lifestyle_emotion_lack_companionship': 'Lack of Companionship',
  'lifestyle_emotion_feel_left_out': 'Feel Left Out',
  'lifestyle_emotion_feel_isolated': 'Feel Isolated',
  'lifestyle_emotion_burnout_emotionally_exhausted': 'Burnout - Emotionally Exhausted',
  'lifestyle_emotion_burnout_cynical': 'Burnout - Cynical',
  'lifestyle_emotion_burnout_cant_disconnect': 'Burnout - Can\'t Disconnect',
  'lifestyle_emotion_burnout_no_breaks': 'Burnout - No Breaks',
  'lifestyle_emotion_burnout_long_hours': 'Burnout - Long Hours',
  'lifestyle_emotion_burnout_personal_time': 'Burnout - Lack of Personal Time',
  'lifestyle_emotion_burnout_family_time': 'Burnout - Lack of Family Time',
  'lifestyle_emotion_burnout_purpose': 'Burnout - Loss of Purpose',
  'lifestyle_emotion_stress_level': 'Perceived Stress Level',

  // === LIFESTYLE - ENVIRONMENT ===
  'lifestyle_environment_smoking_quantity_daily': 'Daily Smoking Quantity',
  'lifestyle_environment_smoking_duration_years': 'Smoking Duration',
  'lifestyle_environment_outdoor_daylight_minutes': 'Outdoor Daylight Exposure',
  'lifestyle_environment_nature_time_days_weekly': 'Nature Time (days per week)',

  // COMPLETE: All 491 parameters from parameters.md have been added!
};

/**
 * Get standardized display name for a parameter key with i18n support
 * @param {string} parameterKey - The standardized parameter key (e.g., 'vitals_sbp')
 * @param {string} language - Optional language code (defaults to current i18n language)
 * @returns {string} - The standardized display name in the specified language
 * @throws {Error} - If parameter key is not found (NO FALLBACK MECHANISMS)
 */
export const getParameterDisplayName = (parameterKey, language = null) => {
  // Validate input
  if (!parameterKey || typeof parameterKey !== 'string') {
    throw new Error(`[getParameterDisplayName] Invalid parameter key: ${parameterKey}. Must be a non-empty string.`);
  }

  // Determine language to use
  const currentLanguage = language || i18n.language || 'en';

  // If Vietnamese is requested and translation exists, use Vietnamese
  if (currentLanguage === 'vi' && hasVietnameseTranslation(parameterKey)) {
    try {
      const vietnameseDisplayName = getParameterDisplayNameVi(parameterKey);
      
      return vietnameseDisplayName;
    } catch (error) {
      // Fall back to English if Vietnamese translation fails
      if (currentLanguage === 'vi') {
        console.warn(`⚠️ [VN-WARNING] Vietnamese translation failed for ${parameterKey}, using English`);
      }
    }
  }

  // Check if parameter exists in English mapping
  const displayName = PARAMETER_DISPLAY_NAMES[parameterKey];

  if (!displayName) {
    // NO FALLBACK MECHANISMS - Fail explicitly with clear error message
    throw new Error(
      `[getParameterDisplayName] Parameter key '${parameterKey}' not found in display name mappings. ` +
      `This parameter must be added to PARAMETER_DISPLAY_NAMES in parameterDisplayNames.js. ` +
      `Available parameters: ${Object.keys(PARAMETER_DISPLAY_NAMES).length} total.`
    );
  }
  
  return displayName;
};

/**
 * Check if a parameter key has a defined display name
 * @param {string} parameterKey - The parameter key to check
 * @returns {boolean} - True if parameter has a display name defined
 */
export const hasParameterDisplayName = (parameterKey) => {
  return parameterKey && typeof parameterKey === 'string' && PARAMETER_DISPLAY_NAMES.hasOwnProperty(parameterKey);
};

/**
 * Get all available parameter keys that have display names defined
 * @returns {string[]} - Array of all parameter keys with display names
 */
export const getAllParameterKeys = () => {
  return Object.keys(PARAMETER_DISPLAY_NAMES);
};

/**
 * Get parameter count statistics
 * @returns {object} - Statistics about parameter coverage
 */
export const getParameterStats = () => {
  const allKeys = Object.keys(PARAMETER_DISPLAY_NAMES);
  const categories = {};
  
  allKeys.forEach(key => {
    const category = key.split('_')[0];
    categories[category] = (categories[category] || 0) + 1;
  });

  return {
    totalParameters: allKeys.length,
    categoryCounts: categories,
    categories: Object.keys(categories)
  };
};

/**
 * Validate parameter naming convention
 * @param {string} parameterKey - The parameter key to validate
 * @returns {object} - Validation result with details
 */
export const validateParameterKey = (parameterKey) => {
  if (!parameterKey || typeof parameterKey !== 'string') {
    return {
      isValid: false,
      error: 'Parameter key must be a non-empty string'
    };
  }

  const parts = parameterKey.split('_');
  if (parts.length < 2) {
    return {
      isValid: false,
      error: 'Parameter key must follow format: category_subcategory_parameter_name (minimum 2 parts)'
    };
  }

  const [category, subcategory, ...parameterParts] = parts;
  const validCategories = ['vitals', 'biometrics', 'pe', 'imaging', 'labs', 'lifestyle'];
  
  if (!validCategories.includes(category)) {
    return {
      isValid: false,
      error: `Invalid category '${category}'. Must be one of: ${validCategories.join(', ')}`
    };
  }

  return {
    isValid: true,
    category,
    subcategory,
    parameterName: parameterParts.join('_')
  };
};

export default {
  getParameterDisplayName,
  hasParameterDisplayName,
  getAllParameterKeys,
  getParameterStats,
  validateParameterKey
};
